/**
 * MonitoringCharts.tsx — Spec 206 / 254. The Dashboard time-series charts (recharts only,
 * design-blueprint kit + tokens). Each chart sits in a DashboardPanel that carries a title
 * AND a one-line description of what it shows and how to read it (Spec 254 — "explain what
 * I'm looking at"). Series colors use CSS var() tokens so they track light/dark mode.
 * Author: Peter Dobler. 2026-06-30 / 2026-07-24.
 */
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { DashboardPanel, chartTooltipStyle } from '@/components/ui';
import type { DashboardResponse } from '@shared/types/pipelineMonitoring';
import { formatEasternDate } from '@/utils/dateFormatter';

function fmtBucket(b: string): string {
  const d = new Date(b);
  return isNaN(d.getTime()) ? b : formatEasternDate(d);
}

const AXIS_TICK = { fontSize: 11, fill: 'var(--color-text-secondary)' };
const GRID = 'var(--color-border)';

function NoData(): JSX.Element {
  return <div className="flex h-[200px] items-center justify-center text-sm text-[var(--color-text-secondary)]">No data in window.</div>;
}

export function MonitoringCharts({ charts }: { charts: DashboardResponse['charts'] }): JSX.Element {
  const sf = charts.successFailureOverTime.map((p) => ({ bucket: fmtBucket(p.bucket), success: p.success ?? 0, failure: p.failure ?? 0 }));
  const dur = charts.durationTrend.map((p) => ({ bucket: fmtBucket(p.bucket), minutes: p.observedMs ? Math.round((p.observedMs / 60000) * 10) / 10 : 0 }));
  const vol = charts.runVolume.map((p) => ({ bucket: fmtBucket(p.bucket), runs: p.runs ?? 0 }));

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <DashboardPanel title="Success vs failure" subtitle="Healthy vs failed runs per day. A growing red band means failures are rising.">
        {sf.length === 0 ? <NoData /> : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={sf} margin={{ top: 4, right: 12, bottom: 0, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="bucket" tick={AXIS_TICK} interval="preserveStartEnd" />
              <YAxis tick={AXIS_TICK} width={34} allowDecimals={false} />
              <Tooltip {...chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="success" stackId="1" name="Success" stroke="var(--color-status-success)" fill="var(--color-status-success)" fillOpacity={0.25} />
              <Area type="monotone" dataKey="failure" stackId="1" name="Failure" stroke="var(--color-status-danger)" fill="var(--color-status-danger)" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </DashboardPanel>

      <DashboardPanel title="Average run duration" subtitle="Mean run time per day (minutes). A rising line means runs are getting slower.">
        {dur.length === 0 ? <NoData /> : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={dur} margin={{ top: 4, right: 12, bottom: 0, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="bucket" tick={AXIS_TICK} interval="preserveStartEnd" />
              <YAxis tick={AXIS_TICK} width={34} />
              <Tooltip {...chartTooltipStyle} formatter={(v) => [`${v as number} min`, 'Avg duration'] as [string, string]} />
              <Line type="monotone" dataKey="minutes" name="Avg duration" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </DashboardPanel>

      <DashboardPanel title="Run volume" subtitle="Runs started per day. An unexpected drop to zero can signal a missed schedule.">
        {vol.length === 0 ? <NoData /> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={vol} margin={{ top: 4, right: 12, bottom: 0, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="bucket" tick={AXIS_TICK} interval="preserveStartEnd" />
              <YAxis tick={AXIS_TICK} width={34} allowDecimals={false} />
              <Tooltip {...chartTooltipStyle} />
              <Bar dataKey="runs" name="Runs" fill="var(--color-accent)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </DashboardPanel>
    </div>
  );
}
